import { Component, ChangeDetectionStrategy, computed, inject, signal } from '@angular/core';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { toSignal } from '@angular/core/rxjs-interop';
import { WidgetsService } from '@components/widgets/widgets.service';
import { marker as _translate } from '@biesbjerg/ngx-translate-extract-marker';
import { VehicleStore } from '@modules/core/store/vehicle.store';
import { AppStateService } from '@modules/core/services/app-state.service';
import { TranslateService } from '@modules/core/services/translate.service';
import { TranslateModule } from '@ngx-translate/core';
import { Vehicle } from '@modules/shared/models/vehicle';
import { BaseWidgetDirective } from '@components/widgets/widgetsbase.directive';
import { WidgetDropdownDirective } from '@components/widgets/widget-dropdown.directive';
import { SpinnerComponent } from '@modules/shared/components/spinner/spinner.component';
import { DeviationStyle, deviationStyle } from '@components/widgets/utils/departure-deviation-colors';
import { MatTooltipModule } from '@angular/material/tooltip';

enum EarlyDepFilterStop {
    all = 'all',
    timingPoint = 'timingPoint',
    startStop = 'startStop'
}

const FILTER_STATE_KEY = 'oms.widget.filter.early-departures';

@Component({
    selector: 'app-early-departures-widget',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SpinnerComponent, TranslateModule, BaseWidgetDirective, DragDropModule, MatTooltipModule, WidgetDropdownDirective],
    templateUrl: './early-departures-widget.component.html',
    styleUrls: ['./early-departures-widget.component.scss'],
})
export class EarlyDeparturesWidgetComponent {
    public readonly widgetState = inject(WidgetsService);
    private readonly _ts = inject(TranslateService);
    private readonly _vehicleStore = inject(VehicleStore);
    private readonly _appState = inject(AppStateService);
    public readonly widgetTitle = signal<string>('');
    public readonly widgetId = signal<string>('widget-early-departures');
    public readonly fillRemaining = signal<boolean>(true);
    public readonly fixedSize = signal<boolean>(false);
    public readonly defaultFraction = signal<number>(0.25);
    public readonly sortCol = signal<'inv' | 'route' | 'stop' | 'dev'>('dev');
    public readonly sortDir = signal<'asc' | 'desc'>('desc');
    public readonly FilterStop = EarlyDepFilterStop;
    public readonly stopFilter = signal<EarlyDepFilterStop>(EarlyDepFilterStop.all);
    public readonly minDeviationMinutes = signal<number>(0);
    public readonly filterActive = computed(() =>
        this.stopFilter() !== EarlyDepFilterStop.all || this.minDeviationMinutes() > 0
    );

    public readonly earlyDepartures = computed<Vehicle[]>(
        () => {
            const vehicles = this._vehicles();
            if (vehicles == undefined) return undefined;
            const col = this.sortCol();
            const dir = this.sortDir();
            const stop = this.stopFilter();
            const minMins = this.minDeviationMinutes();

            const filtered = vehicles.filter(v => {
                const devSecs = v.departureDeviation?.deviation.getTotalSeconds();
                if (devSecs == undefined || devSecs <= 0) return false;
                if (minMins > 0 && devSecs < minMins * 60) return false;
                if (stop === EarlyDepFilterStop.startStop && v.departureDeviation?.sequenceNumber !== 1) return false;
                if (stop === EarlyDepFilterStop.timingPoint && !v.departureDeviation?.isTimingPoint) return false;
                return true;
            });

            return filtered.sort((a, b) => {
                let cmp = 0;
                switch (col) {
                    case 'inv':   cmp = a.inventoryNumberWithoutPrefix.localeCompare(b.inventoryNumberWithoutPrefix); break;
                    case 'route': cmp = (a.routeNumber ?? '').localeCompare(b.routeNumber ?? ''); break;
                    case 'stop':  cmp = (a.departureDeviation?.stopName ?? '').localeCompare(b.departureDeviation?.stopName ?? ''); break;
                    case 'dev':   cmp = a.departureDeviation.deviation.getTotalSeconds() - b.departureDeviation.deviation.getTotalSeconds(); break;
                }
                return dir === 'asc' ? cmp : -cmp;
            });
        }
    );

    private readonly _vehicles = toSignal(
        this._vehicleStore.getVehiclesState(),
        { initialValue: undefined }
    );

    constructor() {
        this.widgetTitle.set(this._ts.instant(_translate('Early departures')));
        const saved = this._loadState();
        this.stopFilter.set(saved.stopFilter);
        this.minDeviationMinutes.set(saved.minDeviationMinutes);
    }

    public setSort(col: 'inv' | 'route' | 'stop' | 'dev'): void {
        if (this.sortCol() === col) {
            this.sortDir.update(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            this.sortCol.set(col);
            this.sortDir.set('asc');
        }
    }

    public setStopFilter(value: EarlyDepFilterStop): void {
        this.stopFilter.set(value);
        this._saveState();
    }

    public increaseMinDeviation(): void {
        this.minDeviationMinutes.update(v => v + 1);
        this._saveState();
    }

    public decreaseMinDeviation(): void {
        this.minDeviationMinutes.update(v => Math.max(0, v - 1));
        this._saveState();
    }

    public setMinDeviation(event: Event): void {
        const raw = (event.target as HTMLInputElement).value;
        const value = Math.max(0, Math.floor(Number(raw)) || 0);
        this.minDeviationMinutes.set(value);
        this._saveState();
    }

    public onDeviationKeyDown(event: KeyboardEvent): void {
        const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
        if (!allowed.includes(event.key) && !/^\d$/.test(event.key)) {
            event.preventDefault();
        }
    }

    public resetFilter(): void {
        this.stopFilter.set(EarlyDepFilterStop.all);
        this.minDeviationMinutes.set(0);
        this._saveState();
    }

    public departureDeviationStyle(v: Vehicle): DeviationStyle {
        const secs = v.departureDeviation?.deviation?.getTotalSeconds();
        if (secs == undefined) return undefined;
        return deviationStyle(secs, this._appState.stateSnapshot.punctualityConfig);
    }

    private _loadState(): { stopFilter: EarlyDepFilterStop; minDeviationMinutes: number } {
        try {
            const raw = localStorage.getItem(FILTER_STATE_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                return {
                    stopFilter: parsed.stopFilter ?? EarlyDepFilterStop.all,
                    minDeviationMinutes: parsed.minDeviationMinutes ?? 0
                };
            }
        } catch { /* ignore */ }
        return { stopFilter: EarlyDepFilterStop.all, minDeviationMinutes: 0 };
    }

    private _saveState(): void {
        localStorage.setItem(FILTER_STATE_KEY, JSON.stringify({
            stopFilter: this.stopFilter(),
            minDeviationMinutes: this.minDeviationMinutes()
        }));
    }
}
